/**
 * Workspace Selector dropdown.
 *
 * ALWAYS visible for authenticated users (even with a single workspace).
 * Dropdown opens when user has other workspaces to switch to.
 * On select: calls Central switch-context directly, updates state, resets cache.
 *
 * v2.3.0: Workspace list comes from switch-context response (otherWorkspaces).
 */

import { useState, useRef, useEffect } from 'react';
import { useWorkspace } from '../hooks/useWorkspace';

export function WorkspaceSelector() {
  const { currentWorkspace, workspaces, switchWorkspace, isLoading, hasMultipleWorkspaces } = useWorkspace();
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click — must be before any early returns (rules of hooks)
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render if there are no workspaces at all
  if (!currentWorkspace && workspaces.length === 0) {
    return null;
  }

  const displayName = currentWorkspace?.name ?? 'Select Workspace';
  const displayInitial = currentWorkspace?.name?.[0]?.toUpperCase() ?? 'W';
  const canOpen = hasMultipleWorkspaces || (!currentWorkspace && workspaces.length > 0);

  const handleSelect = async (workspaceId: string) => {
    setError('');
    const result = await switchWorkspace(workspaceId);
    if (result.success) {
      setIsOpen(false);
    } else {
      setError(result.error || 'Failed to switch workspace');
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => canOpen && setIsOpen(!isOpen)}
        disabled={isLoading}
        className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors ${
          canOpen ? 'hover:bg-gray-200 cursor-pointer' : 'cursor-default'
        }`}
      >
        {isLoading ? (
          <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full" />
        ) : (
          <span className="w-5 h-5 rounded bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-700">
            {displayInitial}
          </span>
        )}
        <span className="max-w-[120px] truncate">{displayName}</span>
        {canOpen && (
          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          <div className="px-3 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
            Workspaces
          </div>

          {workspaces.map((ws) => (
            <button
              key={ws.id}
              type="button"
              onClick={() => handleSelect(ws.id)}
              disabled={isLoading}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                currentWorkspace && ws.id === currentWorkspace.id ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
              }`}
            >
              <span className="w-6 h-6 rounded bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0">
                {ws.name[0]?.toUpperCase()}
              </span>
              <div className="flex-1 text-left min-w-0">
                <div className="truncate font-medium">{ws.name}</div>
                <div className="text-xs text-gray-400 capitalize">{ws.role}</div>
              </div>
              {currentWorkspace && ws.id === currentWorkspace.id && (
                <svg className="w-4 h-4 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}

          {error && (
            <div className="px-3 py-2 text-xs text-red-600 border-t border-gray-100">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
